<template>
  <div
    tabindex="1"
    class="select-container"
    ref="selectContainer"
    @blur="handleBlur"
  >
    <div
      ref="mainContainer"
      :class="[
        disabled ? 'bg-accent-disabled-multiselect' : 'bg-accent-multiselect',
        danger ? 'danger' : '',
        isValid ? 'bg-accent-multiselect' : 'bg-accent-danger-multiselect',
        isBusy ? 'opacity-50 cursor-not-allowed animate-bounce' : ''
      ]"
      @click="!isOptionShown ? showOptions() : closeOptions()"
    >
      <div class="w-11/12 flex flex-row items-center">
        <span
          class="w-11/12 min-w-20 scroll-text"
          :class="[(selected && selected.text) ? 'text-black' : 'text-grey-001']"
        >{{ (selected && selected.text) ? selected.text : placeholder }}</span>
      </div>
      <div class="w-1/12 flex flex-row items-center justify-end">
        <vs-icon
          :icon="!clearable || !value ? (isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down') : (isHovered ? 'close' : isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down')"
          :color="disabled ? '#AAAAAA': '#493493'"
          @mouseover="!!value && !disabled && clearable ? isHovered = true : null"
          @mouseleave="isHovered = false"
          @click="clearable && value ? clear() : null"
        />
      </div>
    </div>
    <span
      v-if="danger"
      class="text-danger"
      style="font-size: .65rem;"
    >* Required</span>
    <div
      v-if="isOptionShown"
      class="bg-option-container"
    >
      <div
        v-if="!hideSearch"
        class="flex gap-2 items-center "
      >
        <div :class="[options.length ? 'bg-option-input-container' : 'bg-option-input-disabled-container', 'w-full']">
          <div class="flex flex-grow flex-col items-start justify-center relative">
            <vs-icon
              class="icon-search"
              icon="search"
              :color="options.length ? '#493493' : '#AAAAAA'"
            />
            <input
              ref="searchInput"
              v-model="search"
              placeholder="Search here..."
              class="bg-option-input"
              :disabled="!options.length"
              @blur="handleBlur"
            >
          </div>
        </div>
        <div
          v-if="isNeedRefresh"
          id="sync-loading"
          class="icon-refresh-container cursor-pointer vs-con-loading__container"
          @click="$emit('refresh')"
        >
          <vs-icon
            icon="sync"
            :color="'#fff'"
          />
        </div>
      </div>
      <div class="mt-3">
        <div class="bg-inner-option-container">
          <div
            v-if="!options.length"
            class="flex flex-row items-center justify-center"
          >
            <span class="empty-query">Nothing can be searched</span>
          </div>
          <div
            v-else-if="!innerOptions.length"
            class="flex flex-row items-center justify-center"
          >
            <span class="empty-query">We can't find item that match your description</span>
          </div>
          <div v-else>
            <ul>
              <template v-for="(item, index) of innerOptions">
                <li
                  v-if="!item.children"
                  class="my-1"
                  :key="index"
                  :class="{'active': isOptionSelected(item)}"
                  @click.stop="selectValue(item)"
                >
                  <span>{{ item.text }}</span>
                </li>
                <template v-else>
                  <div
                    class="my-1 font-bold"
                    :key="index"
                  >
                    <span>{{ item.text }}</span>
                  </div>
                  <li
                    v-for="(child, cIndex) in item.children"
                    :key="`${index}_${cIndex}`"
                    class="my-1"
                    :class="{'active': isOptionSelected(child)}"
                    @click.stop="selectValue(child)"
                  >
                    {{ child.text }}
                  </li>
                </template>
              </template>
            </ul>
          </div>
          <vs-button
            v-if="isNeedButtonLink"
            class="button shadow-primary mt-6"
            style="min-width: 100%"
            color="primary"
            @click="$emit('link')"
          >
            {{ buttonLinkText }}
          </vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isBusy: {
      type: Boolean,
      default: () => false
    },
    value: {
      type: null,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => 'Select option'
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    hideSearch: {
      type: Boolean,
      default: () => false
    },
    isObjectModel: {
      type: Boolean,
      default: () => false
    },
    isGrouping: {
      type: Boolean,
      default: () => false
    },
    isValid: {
      type: Boolean,
      default: () => true
    },
    /**
     * Array of available options: Objects.
     * Objects must consist of `value` and `text`
     * `value` passed for `v-model`
     * example: `{ value: 'something', text: 'something' }`
     */
    options: {
      type: Array,
      default: () => []
    },
    danger: {
      type: Boolean,
      default: () => false
    },
    clearable: {
      type: Boolean,
      default: () => true
    },
    bottom: {
      type: Boolean,
      default: false
    },
    isNeedRefresh: {
      type: Boolean,
      default: () => false
    },
    isNeedButtonLink: {
      type: Boolean,
      default: () => false
    },
    isLoadingRefresh: {
      type: Boolean,
      default: () => false
    },
    buttonLinkText: {
      type: String,
      default: () => 'Go to Master Data'
    }
  },
  data() {
    return {
      isOptionShown: false,
      isHovered: false,
      search: null,
      innerOptions: this.options,
      selected: this.isObjectModel
        ? this.value
        : {
          value: this.value,
          text: this.getText()
        }
    }
  },
  watch: {
    search: {
      handler(val) {
        if (val !== null && !val.length) {
          this.innerOptions = this.options
          return
        }
        if(this.isGrouping) {
          this.innerOptions = this.options.map(item => {
            if(`${item.text}`?.toLowerCase().includes(val?.toLowerCase())){
              return item
            } else {
              const selectedChildren = item.children.filter(ch => `${ch.text}`?.toLowerCase().includes(val?.toLowerCase()))
              if(selectedChildren.length) {
                return {...item, children: selectedChildren }
              }
              return null
            }
          }).filter(item => item)
        } else {
          this.innerOptions = this.options.filter(item => `${item.text}`?.toLowerCase().includes(val?.toLowerCase()))
        }
      }
    },
    options: {
      handler(val) {
        if (!this.search) {
          this.innerOptions = val
        }
      }
    },
    value: {
      handler(val) {
        this.selected = this.isObjectModel
          ? val
          : {
            value: val,
            text: this.getText()
          }
      }
    },
    isLoadingRefresh: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.$vs.loading({
              color: 'secondary',
              container: '#sync-loading',
              scale: 0.35
            })
          })
        } else {
          this.$vs.loading.close('#sync-loading > .con-vs-loading')
        }
      }
    }
  },
  methods: {
    showOptions() {
      if (!this.disabled && !this.isOptionShown && !this.isHovered) {
        this.isOptionShown = true;

        // Calculate the position and height of the dropdown
        this.$nextTick(() => {
          if (!this.hideSearch) {
            const searchInput = this.$refs.searchInput
            searchInput.focus()
          }

          const dropdown = this.$el.querySelector('.bg-option-container');
          const dropdownInner = this.$el.querySelector('.bg-inner-option-container');
          const inputRect = this.$el.getBoundingClientRect();
          const dropdownHeight = dropdown.offsetHeight;

          // Find the vs-con-table container
          const vsConTable = this.$el.closest('.vs-con-table');
          let containerHeight = window.innerHeight;
          let containerTop = 0;

          if (vsConTable) {
            const tableRect = vsConTable.getBoundingClientRect();
            containerHeight = tableRect.height;
            containerTop = tableRect.top;
          }

          // Calculate space above and below relative to the container
          const spaceBelow = containerHeight - (inputRect.bottom - containerTop);
          const spaceAbove = inputRect.top - containerTop;

          // Determine the best position and max height
          if (this.bottom) {
            // Always position below
            dropdown.style.top = '100%';
            dropdown.style.bottom = 'auto';
            dropdown.style.maxHeight = `${spaceBelow - 30}px`;
            dropdownInner.style.maxHeight = `${spaceBelow - 80}px`;
          } else if (spaceAbove > spaceBelow) {
            // Position above the input
            dropdown.style.top = 'auto';
            dropdown.style.bottom = '100%';
            dropdown.style.maxHeight = `${spaceAbove - 30}px`;
            dropdownInner.style.maxHeight = `${spaceAbove - 80}px`;
          } else {
            // Position below the input
            dropdown.style.top = '100%';
            dropdown.style.bottom = 'auto';
            dropdown.style.maxHeight = `${spaceBelow - 30}px`;
            dropdownInner.style.maxHeight = `${spaceBelow - 80}px`;
          }
        });

        this.$el.focus();
      }
    },
    closeOptions() {
      if (!this.disabled && this.isOptionShown) {
        this.isOptionShown = false
        this.$el.blur()
        this.$emit('on-close-options')
      }
    },
    handleBlur(ev) {
      if (!this.$refs.selectContainer.contains(ev.relatedTarget)) {
        this.closeOptions();
      }
    },
    selectValue(item) {
      this.selected = item
      this.$emit('input', this.isObjectModel
        ? this.selected
        : this.selected.value
      )

      this.closeOptions()
    },
    getText() {
      if (this.value) {
        if(this.isGrouping) {
          const selectedOptions = this.options.find(item => item.children.find(ch => ch.value === this.value))
          const selectedChildren = selectedOptions.children.find(sc => sc.value === this.value)
          return selectedOptions
            ? selectedChildren.text
            : null
        }
        return this.options.length
          ? this.options.find(item => item.value === this.value)?.text
          : null
      }
      return null
    },
    isOptionSelected(item) {
      if (this.selected) {
        return this.selected.value === item.value
      }

      return this.selected
    },
    clear() {
      if (this.value && this.isHovered && this.clearable) {
        this.isHovered = false
        this.$el.blur()
        this.$emit('input', null)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.danger {
  border: 1px solid rgba(var(--vs-danger),1)!important;
}

li {
  @apply p-3 rounded-lg cursor-pointer;

  color: #3D3D3D;

  &:hover {
    @apply font-semibold;

    background-color: #F1F1FD;
    color: #6546C3;
  }
}

.select-container {
  @apply w-full relative;
}

.bg-accent-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-accent-disabled-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-not-allowed;

  height: calc(1rem * 10 / 3);
  background-color: #ECEBED;
}

.bg-accent-danger-multiselect {
  @apply p-3 w-full flex flex-row border-2 border-danger rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-option-container {
  @apply p-3 mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg overflow-hidden;

  max-height: 20rem;
  z-index: 1000;
  top: 100%;
  left: 0;
  transform: translateY(0.25rem);
}

.bg-option-input-container {
  @apply flex flex-row items-center rounded-lg;

  min-height: 3rem;
  background-color: #F1F1FD;
}

.bg-option-input-disabled-container {
  @apply flex flex-row items-center rounded-lg;

  min-height: 3rem;
  background-color: #ECEBED;
}

.bg-option-input {
  @apply w-full bg-transparent p-4;
  border-radius: 0.5rem !important;
  padding-left: calc(1rem + 24px) !important;

  &:disabled {
    @apply cursor-not-allowed;
  }
}

.bg-inner-option-container {
  @apply overflow-auto;

  height: 15rem;
}

.icon-search {
  @apply absolute cursor-default;

  top: auto;
  left: 0.5rem;
}

.empty-query {
  @apply italic text-center;

  color: #3D3D3D;
}

.active {
  @apply font-semibold;

  background-color: #F1F1FD;
  color: #6546C3;
}

.scroll-text {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.scroll-text::-webkit-scrollbar {
  display: none; /* Chrome, Safari */
}

.icon-refresh-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0B9DDE;
  border-radius: 3px;
  padding: 1px;
}
</style>
